import React from 'react'
import type { ChatMessage, CreationStatus } from '@/types/Dashboards.types'
import { deriveCreationStep } from './deriveCreationStep'

interface StepProgressIndicatorProps {
  messages: ChatMessage[]
  creationStatus: CreationStatus | null
  totalSteps?: number
}

/**
 * StepProgressIndicator — shown in the creation-mode chat header.
 * Renders filled dots for steps completed up to and including the current step.
 * Step derivation logic lives in deriveCreationStep.ts per brand guide Section 12.
 *
 * totalSteps defaults to 5 for backward compatibility with existing canvas sessions.
 * Pass totalSteps={4} for the /dashboards/new full-screen creation flow.
 */
export const StepProgressIndicator: React.FC<StepProgressIndicatorProps> = ({
  messages,
  creationStatus,
  totalSteps,
}) => {
  const total = totalSteps ?? 5
  const currentStep = deriveCreationStep(messages, creationStatus, total)

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="text-xs text-secondary font-medium">
        Step {currentStep} of {total}
      </span>
      <div
        className="flex gap-1"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuetext={`Step ${currentStep} of ${total}`}
        aria-label="Dashboard creation progress"
      >
        {Array.from({ length: total }, (_, i) => i + 1).map(step => (
          <div
            key={step}
            data-testid="step-dot"
            className={`w-6 h-1.5 rounded-full ${step <= currentStep ? 'bg-accent' : 'bg-border'}`}
          />
        ))}
      </div>
    </div>
  )
}
